import styled from 'styled-components';
import { Link } from 'react-router-dom';

import { breakpoints } from 'theme';

/** Aligns with profile Content (dew) */
export const CheckoutBackdrop = styled.div`
  background: ${({ theme }) => theme.color.dew};
  min-height: calc(100vh - 80px);
  width: 100%;
  box-sizing: border-box;
`;

/** Matches profile subscription header title next to back control */
export const CheckoutHeaderTitle = styled.span`
  margin-left: 14px;
  font-weight: 600;

  color: ${({ theme }) => theme.color.pickledBluewood};
`;

export const CheckoutPageWrap = styled.div`
  max-width: 820px;
  width: 100%;
  margin: 0 auto;
  padding: 24px 25px 56px;
  box-sizing: border-box;
  color: ${({ theme }) => theme.color.pickledBluewood};

  @media ${breakpoints.downSmPlus} {
    padding: 20px 16px 48px;
  }
`;

export const CheckoutBreadcrumb = styled.nav`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.color.baliHai};
  margin-bottom: 24px;
`;

export const CheckoutBreadcrumbLink = styled(Link)`
  color: ${({ theme }) => theme.color.baliHai};
  text-decoration: none;
  &:hover {
    color: ${({ theme }) => theme.color.brightTurquoise};
  }
`;

export const CheckoutBreadcrumbSep = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.color.baliHai};
`;

export const CheckoutBreadcrumbCurrent = styled.span`
  color: ${({ theme }) => theme.color.BlueBayoux};
  font-weight: 700;
`;

export const ProgressBar = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 40px;
`;

export const ProgStep = styled.div<{ $doneConnector?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 13px;
    left: 50%;
    width: 100%;
    height: 2px;
    background: ${({ theme, $doneConnector }) =>
      $doneConnector ? theme.color.brightTurquoise : theme.color.porcelain};
    z-index: 0;
  }
`;

export const ProgDot = styled.div<{ $active?: boolean; $done?: boolean }>`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.color.porcelain};
  background: ${({ theme }) => theme.color.white};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.baliHai};
  z-index: 1;
  position: relative;
  transition: all 0.2s;

  ${({ theme, $active }) =>
    $active &&
    `
    border-color: ${theme.color.skobeloffGreen};
    background: ${theme.color.skobeloffGreen};
    color: ${theme.color.white};
  `}

  ${({ theme, $done }) =>
    $done &&
    `
    border-color: ${theme.color.brightTurquoise};
    background: ${theme.color.brightTurquoise};
    color: ${theme.color.white};
  `}
`;

export const ProgLabel = styled.div<{ $active?: boolean; $done?: boolean }>`
  font-size: 11px;
  color: ${({ theme }) => theme.color.baliHai};
  margin-top: 5px;
  text-align: center;

  ${({ theme, $active }) =>
    $active &&
    `
    color: ${theme.color.pickledBluewood};
    font-weight: 600;
  `}

  ${({ theme, $done }) =>
    $done &&
    `
    color: ${theme.color.brightTurquoise};
  `}
`;

export const CheckoutGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(300px, 380px);
  gap: 24px;
  align-items: start;
  margin-bottom: 0;

  @media ${breakpoints.downMdPlus} {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const OrderCard = styled.div`
  background: ${({ theme }) => theme.color.white};
  border: 1.5px solid ${({ theme }) => theme.color.porcelain};
  border-radius: 20px;
  text-align: left;
  padding: 24px;
  margin-bottom: 0;
  box-sizing: border-box;
  min-width: 0;
`;

export const OrderCardTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.pickledBluewood};
  margin: 0 0 18px;
`;

export const PlanSummaryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 20px;
`;

export const PlanIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: ${({ theme }) => theme.color.aquaSqueeze};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

export const PlanInfoName = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.color.pickledBluewood};
`;

export const PlanInfoSub = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.color.baliHai};
  margin-top: 2px;
`;

export const PlanBadge = styled.span<{
  $variant: 'current' | 'new' | 'enterprise';
}>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 40px;
  margin-top: 5px;

  ${({ theme, $variant }) =>
    $variant === 'current'
      ? `
    background: ${theme.color.aquaSqueeze};
    color: ${theme.color.skobeloffGreen};
  `
      : $variant === 'enterprise'
      ? `
    background: ${theme.color.skobeloffGreen};
    color: #fff;
  `
      : `
    background: #f3f0ff;
    color: #5340b7;
  `}
`;

export const SpecPills = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: 20px;
`;

export const SpecPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: ${({ theme }) => theme.color.aquaSqueeze};
  border: 1.5px solid ${({ theme }) => theme.color.porcelain};
  border-radius: 40px;
  padding: 5px 12px;
  font-size: 12px;
  color: ${({ theme }) => theme.color.BlueBayoux};
  font-weight: 500;
`;

export const SpecPillVal = styled.span`
  font-weight: 700;
  color: ${({ theme }) => theme.color.pickledBluewood};
`;

export const PriceBreakdown = styled.div`
  border-top: 1px solid ${({ theme }) => theme.color.porcelain};
  padding-top: 16px;
`;

export const PbRow = styled.div<{ $highlight?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 13px;
  padding: 5px 0;

  ${({ theme, $highlight }) =>
    $highlight &&
    `
    background: ${theme.color.aquaSqueeze};
    border-radius: 6px;
    padding: 6px 10px;
    margin: 2px -10px;

    span:first-child {
      color: ${theme.color.skobeloffGreen};
      font-weight: 500;
    }
    span:last-child {
      color: ${theme.color.skobeloffGreen};
      font-weight: 700;
    }
  `}
`;

export const PbLabel = styled.span`
  color: ${({ theme }) => theme.color.BlueBayoux};
`;

export const PbVal = styled.span`
  color: ${({ theme }) => theme.color.pickledBluewood};
  font-weight: 500;
`;

export const PbDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.color.porcelain};
  margin: 10px 0;
`;

export const PbRowTotal = styled(PbRow)`
  font-size: 15px;
  margin-top: 4px;

  ${PbLabel} {
    color: ${({ theme }) => theme.color.pickledBluewood};
    font-weight: 600;
  }

  ${PbVal} {
    font-size: 19px;
    font-weight: 700;
  }
`;

export const IncludedSection = styled.div`
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.color.porcelain};
`;

export const IncludedTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: ${({ theme }) => theme.color.baliHai};
  margin-bottom: 10px;
`;

export const IncludedList = styled.ul`
  list-style: none;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 12px;
  margin: 0;
  padding: 0;

  @media ${breakpoints.downSmPlus} {
    grid-template-columns: 1fr;
  }
`;

export const IncludedItem = styled.li`
  font-size: 12px;
  color: ${({ theme }) => theme.color.BlueBayoux};
  display: flex;
  gap: 6px;
  align-items: flex-start;
  line-height: 1.4;
  padding: 3px 0;
`;

export const IncludedCheck = styled.span`
  color: ${({ theme }) => theme.color.brightTurquoise};
  font-size: 13px;
  flex-shrink: 0;
  margin-top: 1px;
`;

export const InfoBox = styled.div`
  background: ${({ theme }) => theme.color.aquaSqueeze};
  border: 1px solid ${({ theme }) => theme.color.botticelli};
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12px;
  color: ${({ theme }) => theme.color.skobeloffGreen};
  line-height: 1.55;
  margin-top: 14px;
  display: flex;
  gap: 8px;
`;

export const TrustRow = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 16px;
  flex-wrap: wrap;
`;

export const TrustItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: ${({ theme }) => theme.color.baliHai};
`;

export const PaymentMethodSection = styled.div`
  margin-bottom: 20px;
`;

export const PaymentMethodLegend = styled.div`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.color.baliHai};
  margin-bottom: 12px;
`;

export const PaymentMethodGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px;
  align-items: stretch;

  @media ${breakpoints.downSmPlus} {
    grid-template-columns: minmax(0, 1fr);
    gap: 12px;
  }
`;

export const PaymentMethodCard = styled.button<{ $selected?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0;
  width: 100%;
  min-width: 0;
  height: 100%;
  padding: 0;
  border-radius: 14px;
  border: 1.5px solid
    ${({ theme, $selected }) =>
      $selected ? theme.color.skobeloffGreen : theme.color.porcelain};
  background: ${({ theme }) => theme.color.white};
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, box-shadow 0.15s;
  box-sizing: border-box;
  overflow: hidden;

  &:hover {
    border-color: ${({ theme }) => theme.color.casper};
  }

  ${({ $selected, theme }) =>
    $selected &&
    `
    box-shadow: 0 0 0 1px ${theme.color.skobeloffGreen}33;
  `}
`;

export const PaymentMethodCardTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  flex-shrink: 0;
`;

export const PaymentBrandStrip = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-top: 1px solid ${({ theme }) => theme.color.porcelain};
  padding: 8px 8px 10px;
  background: ${({ theme }) => theme.color.aquaSqueeze};
  min-height: 56px;
`;

export const PaymentBrandRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 6px 8px;

  svg {
    width: auto;
    height: 20px;
    flex-shrink: 0;
    display: block;
  }
`;

export const InvoiceOptionStrip = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-top: 1px solid ${({ theme }) => theme.color.porcelain};
  padding: 8px 8px 10px;
  background: ${({ theme }) => theme.color.blackSqueeze};
  min-height: 56px;
`;

export const PaymentMethodRadioUi = styled.span<{ $checked?: boolean }>`
  width: 15px;
  height: 15px;
  border-radius: 50%;
  border: 2px solid
    ${({ theme, $checked }) =>
      $checked ? theme.color.skobeloffGreen : theme.color.porcelain};
  flex-shrink: 0;
  margin-top: 1px;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ theme, $checked }) =>
      $checked ? theme.color.skobeloffGreen : 'transparent'};
  }
`;

export const PaymentMethodName = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.pickledBluewood};
  line-height: 1.2;
`;

export const PaymentMethodDesc = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.color.baliHai};
  line-height: 1.2;
`;

export const PaymentMethodText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const InvoicePriceTitle = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${({ theme }) => theme.color.pickledBluewood};
  letter-spacing: -0.02em;
  line-height: 1.2;
`;

export const InvoicePriceSub = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.color.BlueBayoux};
  margin-top: 8px;
  line-height: 1.5;
`;

export const AmberInfoBox = styled.div`
  background: ${({ theme }) => theme.color.koromiko}24;
  border: 1px solid ${({ theme }) => theme.color.koromiko};
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 12px;
  color: ${({ theme }) => theme.color.BlueBayoux};
  line-height: 1.55;
  margin-top: 14px;
  text-align: left;
`;

/** Invoice + monthly billing: single prominent notice (annual required) */
export const InvoiceYearlyOnlyBanner = styled.div`
  background: ${({ theme }) => theme.color.koromiko}55;
  border: 2px solid ${({ theme }) => theme.color.koromiko};
  border-radius: 12px;
  padding: 16px 18px;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.pickledBluewood};
  line-height: 1.5;
  margin-top: 14px;
  text-align: left;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.08);
`;

export const InvoiceForm = styled.form`
  text-align: left;
  margin-bottom: 16px;
`;

export const InvoiceFieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.pickledBluewood};
  margin-bottom: 6px;
`;

export const InvoiceHint = styled.p`
  margin: 0 0 10px;
  font-size: 11px;
  color: ${({ theme }) => theme.color.baliHai};
  line-height: 1.45;
`;

export const InvoiceEmailList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 10px;
`;

export const InvoiceEmailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

export const InvoiceTextInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  font-size: 14px;
  border-radius: 10px;
  border: 1.5px solid ${({ theme }) => theme.color.porcelain};
  color: ${({ theme }) => theme.color.pickledBluewood};
  background: ${({ theme }) => theme.color.white};
  box-sizing: border-box;

  &::placeholder {
    color: ${({ theme }) => theme.color.baliHai};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.color.brightTurquoise};
  }
`;

export const BtnInvoiceRowRemove = styled.button`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 10px;
  background: ${({ theme }) => theme.color.blackSqueeze};
  color: ${({ theme }) => theme.color.BlueBayoux};
  font-size: 18px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.color.porcelain};
    color: ${({ theme }) => theme.color.pickledBluewood};
  }
`;

export const BtnInvoiceAddEmail = styled.button`
  display: inline-block;
  margin-bottom: 12px;
  padding: 0;
  border: none;
  background: none;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.brightTurquoise};
  cursor: pointer;
  text-align: left;

  &:hover {
    color: ${({ theme }) => theme.color.skobeloffGreen};
    text-decoration: underline;
  }
`;

export const InvoiceFormError = styled.div`
  font-size: 12px;
  color: #c53030;
  margin-bottom: 10px;
  line-height: 1.4;
`;

export const InvoiceSuccessPanel = styled.div`
  text-align: left;
  padding: 14px 16px;
  border-radius: 12px;
  background: ${({ theme }) => theme.color.aquaSqueeze};
  border: 1.5px solid ${({ theme }) => theme.color.brightTurquoise};
  margin-bottom: 16px;
`;

export const InvoiceSuccessTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.color.pickledBluewood};
  margin-bottom: 8px;
`;

export const InvoiceSuccessAmount = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.color.BlueBayoux};
  margin-bottom: 12px;
`;

export const InvoiceSuccessNote = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.color.BlueBayoux};
  line-height: 1.45;
`;

export const LinkInvoiceHosted = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 12px 16px;
  border-radius: 40px;
  background: ${({ theme }) => theme.color.skobeloffGreen};
  font-size: 14px;
  font-weight: 700;
  color: #fff !important;
  text-decoration: none;
  text-align: center;
  box-sizing: border-box;
  transition: all 0.15s;

  &:hover {
    filter: brightness(1.05);
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(10, 117, 117, 0.25);
  }
`;

export const PaymentCard = styled.div`
  background: ${({ theme }) => theme.color.white};
  border: 1.5px solid ${({ theme }) => theme.color.porcelain};
  border-radius: 20px;
  padding: 24px;
  position: sticky;
  top: 96px;
  box-sizing: border-box;
  min-width: 0;

  @media ${breakpoints.downMdPlus} {
    position: relative;
    top: auto;
  }
`;

export const PaymentCardTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.pickledBluewood};
  margin: 0 0 20px;
`;

export const PriceDisplay = styled.div`
  text-align: center;
  padding: 16px 0 20px;
  border-bottom: 1px solid ${({ theme }) => theme.color.porcelain};
  margin-bottom: 20px;
`;

/** Invoice path - same rhythm as PriceDisplay */
export const InvoicePriceDisplay = styled.div`
  text-align: center;
  padding: 16px 0 12px;
  border-bottom: 1px solid ${({ theme }) => theme.color.porcelain};
  margin-bottom: 20px;
`;

export const PriceBig = styled.div`
  font-size: 44px;
  font-weight: 700;
  color: ${({ theme }) => theme.color.pickledBluewood};
  letter-spacing: -2px;
  line-height: 1;
`;

export const PriceBigSup = styled.sup`
  font-size: 22px;
  font-weight: 600;
  vertical-align: top;
  margin-top: 8px;
  display: inline-block;
`;

export const PricePeriod = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }) => theme.color.baliHai};
`;

export const PriceSub = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.color.baliHai};
  margin-top: 4px;
`;

export const PriceSave = styled.span`
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.BlueBayoux};
  background: ${({ theme }) => theme.color.koromiko}33;
  padding: 3px 9px;
  border-radius: 40px;
  margin-top: 8px;
`;

export const PriceToday = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.color.BlueBayoux};
  margin-top: 12px;
  padding: 10px 14px;
  background: ${({ theme }) => theme.color.blackSqueeze};
  border-radius: 8px;
  line-height: 1.5;
  text-align: left;

  strong {
    color: ${({ theme }) => theme.color.pickledBluewood};
  }
`;

export const PaySummaryRows = styled.div`
  margin-bottom: 20px;
`;

export const PaySummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  padding: 4px 0;
`;

export const PaySummaryLabel = styled.span`
  color: ${({ theme }) => theme.color.BlueBayoux};
`;

export const PaySummaryVal = styled.span`
  color: ${({ theme }) => theme.color.pickledBluewood};
  font-weight: 500;
`;

export const PaySummaryTotalRow = styled(PaySummaryRow)`
  border-top: 1px solid ${({ theme }) => theme.color.porcelain};
  margin-top: 8px;
  padding-top: 10px;

  ${PaySummaryLabel} {
    font-weight: 600;
    color: ${({ theme }) => theme.color.pickledBluewood};
  }

  ${PaySummaryVal} {
    font-weight: 700;
    font-size: 15px;
    color: ${({ theme }) => theme.color.pickledBluewood};
  }
`;

export const BtnPurchase = styled.button`
  width: 100%;
  padding: 15px;
  border-radius: 40px;
  background: ${({ theme }) => theme.color.skobeloffGreen};
  border: none;
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 10px;

  &:hover:not(:disabled) {
    filter: brightness(1.05);
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(10, 117, 117, 0.25);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

export const PayActions = styled.div`
  position: relative;
  margin-bottom: 10px;

  iframe {
    border-radius: 40px;
  }

  & > div {
    width: 100%;
  }
`;

export const PayPalShell = styled.div`
  border-radius: 40px;
  overflow: hidden;
  margin-bottom: 10px;
`;

export const StripeNote = styled.div`
  text-align: center;
  font-size: 11px;
  color: ${({ theme }) => theme.color.baliHai};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

export const Guarantee = styled.div`
  border-top: 1px solid ${({ theme }) => theme.color.porcelain};
  padding-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const GuaranteeItem = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  color: ${({ theme }) => theme.color.BlueBayoux};
`;

export const GuaranteeIcon = styled.span`
  color: ${({ theme }) => theme.color.brightTurquoise};
  font-size: 13px;
  flex-shrink: 0;
`;

export const StyledLinkCheckout = styled.a`
  text-decoration: none;
  color: ${({ theme }) => theme.color.brightTurquoise};
  font-weight: 600;

  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.color.skobeloffGreen};
  }
`;

export const StyledCancelCheckout = styled.span`
  color: ${({ theme }) => theme.color.baliHai};
  text-decoration: none;
  cursor: pointer;
  display: inline-block;
  margin-top: 10px;
  font-size: 12px;

  &:hover {
    color: ${({ theme }) => theme.color.pickledBluewood};
  }
`;

export const CheckoutPlanMissingWrap = styled.div`
  max-width: 640px;
  margin: 0 auto;
  padding: 32px 24px;
`;

export const CheckoutPlanMissingText = styled.p`
  margin-bottom: 16px;
  line-height: 1.5;
  color: ${({ theme }) => theme.color.BlueBayoux};
`;

export const CheckoutPlanMissingLink = styled(Link)`
  color: ${({ theme }) => theme.color.brightTurquoise};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.color.skobeloffGreen};
  }
`;
